/**
 * RetailerOnboardingChart.jsx
 * Shows retailer onboarding count by date.
 * Default: current month, all dates on X-axis.
 * Dropdowns: Month + Year to switch view.
 *
 * Props:
 *   data: Array of { date: "2025-04-03", count: 5 }
 *   (If no prop passed, uses sample data for demo)
 */

import { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";
import { ChevronDown } from "lucide-react";

// ─── Sample data (replace with your API data) ─────────────────────────────────
const SAMPLE_DATA = (() => {
  const rows = [];
  const now = new Date();
  // Generate 3 months of random daily onboarding counts
  for (let m = 0; m < 3; m++) {
    const year = now.getFullYear();
    const month = now.getMonth() - m;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      const dateObj = new Date(year, month, d);
      if (dateObj > now) continue;
      rows.push({
        date: `${year}-${String(dateObj.getMonth() + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
        count: Math.floor(Math.random() * 8),
      });
    }
  }
  return rows;
})();

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-slate-800 text-white text-xs rounded-lg px-3 py-2 shadow-xl">
        <p className="text-slate-300 mb-0.5">{label}</p>
        <p className="font-semibold text-blue-300">{payload[0].value} onboarded</p>
      </div>
    );
  }
  return null;
};

// ─── Small Dropdown ───────────────────────────────────────────────────────────
function MiniSelect({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 hover:border-blue-400 shadow-sm transition font-medium"
      >
        {value} <ChevronDown className="w-3 h-3 text-slate-400" />
      </button>
      {open && (
        <div
          className="absolute z-50 top-full mt-1 right-0 bg-white border border-slate-200 rounded-xl shadow-xl min-w-[120px] max-h-52 overflow-y-auto"
          onMouseLeave={() => setOpen(false)}
        >
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => { onChange(opt); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-xs transition
                ${opt === value
                  ? "bg-blue-50 text-blue-700 font-semibold"
                  : "text-slate-700 hover:bg-blue-50 hover:text-blue-600"
                }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function RetailerOnboardingChart({ data, onMonthChange, selectedMonth, selectedYear}) {
  const now = new Date();
  const [selMonth, setSelMonth] = useState(MONTHS[selectedMonth]);
  const [selYear, setSelYear] = useState(String(selectedYear));

  // Available years derived from data
  const availableYears = useMemo(() => {
    const years = [...new Set(data.map((d) => d.date.slice(0, 4)))].sort((a, b) => b - a);
    return years.length ? years : [String(now.getFullYear())];
  }, [data]);

  // Build chart data: all days of selected month, count = 0 if no data
  const chartData = useMemo(() => {
    const monthIdx  = MONTHS.indexOf(selMonth); // 0-based
    const year      = parseInt(selYear);
    const daysInMon = new Date(year, monthIdx + 1, 0).getDate();
    const today     = new Date();

    return Array.from({ length: daysInMon }, (_, i) => {
      const day     = i + 1;
      const dateStr = `${selYear}-${String(monthIdx + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const found   = data.find((d) => d.date === dateStr);
      const isFuture = new Date(dateStr) > today;
      return {
        label: String(day),       // X-axis label: day number
        date:  dateStr,
        count: found ? found.count : 0,
        isFuture,
      };
    });
  }, [selMonth, selYear, data]);

  const handleMonthChange = (month) => {
  setSelMonth(month);

  if (onMonthChange) {
    const monthIndex = MONTHS.indexOf(month);
    onMonthChange(monthIndex, selYear);
  }
};

const handleYearChange = (year) => {
  setSelYear(year);

  if (onMonthChange) {
    const monthIndex = MONTHS.indexOf(selMonth);
    onMonthChange(monthIndex, year);
  }
};

  const totalMonth = chartData.reduce((s, d) => s + d.count, 0);
  const peakDay    = chartData.reduce((a, b) => (b.count > a.count ? b : a), chartData[0] || {});

  useEffect(() => {
  setSelMonth(MONTHS[selectedMonth]);
  setSelYear(String(selectedYear));
}, [selectedMonth, selectedYear]);

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">

      {/* Header */}
      <div className="flex items-start justify-between mb-3 gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Retailer Onboarding</h3>
          <p className="text-[11px] text-slate-400 mt-0.5">
            Daily onboarding count ·{" "}
            <span className="text-blue-600 font-semibold">{totalMonth}</span> this month
            {peakDay?.count > 0 && (
              <> · Peak: <span className="text-emerald-600 font-semibold">{peakDay.count}</span> on {peakDay.date}</>
            )}
          </p>
        </div>

        {/* Month + Year filters */}
        <div className="flex items-center gap-2">
          <MiniSelect value={selMonth} options={MONTHS} onChange={handleMonthChange} />
          <MiniSelect value={selYear}  options={availableYears} onChange={handleYearChange} />
        </div>
      </div>

      {/* Chart */}
      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            barSize={chartData.length > 28 ? 8 : 12}
            margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
          >
            <CartesianGrid vertical={false} stroke="#f1f5f9" />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: "#94a3b8" }}
              // Show every 5th day label to avoid crowding
              // tickFormatter={(v) => (parseInt(v) % 5 === 1 || parseInt(v) === 1 ? v : "")}
              interval={0}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: "#94a3b8" }}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f8fafc" }} />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    entry.isFuture
                      ? "#e2e8f0"                          // grey for future dates
                      : entry.count === 0
                      ? "#dbeafe"                          // light blue for zero
                      : entry.count === peakDay?.count
                      ? "#1d4ed8"                          // dark blue for peak
                      : "#3b82f6"                          // normal blue
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex gap-4 mt-2 flex-wrap">
        <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
          <span className="w-3 h-3 rounded-sm bg-blue-600 inline-block" /> Onboarded
        </span>
        <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
          <span className="w-3 h-3 rounded-sm bg-blue-100 inline-block" /> No activity
        </span>
        <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
          <span className="w-3 h-3 rounded-sm bg-blue-800 inline-block" /> Peak day
        </span>
      </div>
    </div>
  );
}